import { User } from '@/types/auth';
import { QuestionnaireData } from '@/types/category';
import { sanitizeProfileData, debugNameResolution, validatePersonalizationName } from './nameContaminationBlocker';

export interface NamePersonalizationResult {
  displayName: string;
  isUsingProfileName: boolean;
  usedPreferredName?: boolean;
  nameUsageType?: 'first' | 'preferred' | 'random' | 'group';
}

/**
 * Gets the appropriate name to use for personalization based on user authentication
 */
export const getPersonalizationName = (
  user: User | null,
  formData: QuestionnaireData,
  profileData?: { first_name?: string; preferred_name?: string; name_usage_preference?: string }
): NamePersonalizationResult => {
  // 🚫 CRITICAL: Sanitize profile data first to block contamination
  const cleanProfileData = sanitizeProfileData(profileData);
  
  console.log('🕵️ NAME RESOLUTION START:', {
    hasUser: !!user,
    hasProfileData: !!profileData,
    profileDataSanitized: profileData !== cleanProfileData,
    profileFirstName: cleanProfileData?.first_name,
    profilePreferredName: cleanProfileData?.preferred_name,
    profileNameUsage: cleanProfileData?.name_usage_preference,
    userMetadataFirstName: user?.user_metadata?.first_name,
    userMetadataFullName: user?.user_metadata?.full_name
  });
  
  // 🚫 Early contamination check
  if (profileData?.first_name === 'Vinh' || profileData?.preferred_name === 'Vinh') {
    console.error('🚨 BLOCKED CONTAMINATED PROFILE DATA containing "Vinh"');
    console.error('🚨 Using generic fallback instead');
    return {
      displayName: 'there',
      isUsingProfileName: false
    };
  }

  // STRICT ENFORCEMENT: Only profile data is used for names
  // Biography data is NEVER used for name resolution
  
  // For unauthenticated users, return generic
  if (!user) {
    console.log('🔒 STRICT NAME ENFORCEMENT: No user, using generic');
    return {
      displayName: 'there',
      isUsingProfileName: false
    };
  }

  // For authenticated users, PROFILE DATA IS THE ABSOLUTE AUTHORITY
  if (cleanProfileData) {
    const firstName = cleanProfileData.first_name?.trim();
    const preferredName = cleanProfileData.preferred_name?.trim();
    const preference = cleanProfileData.name_usage_preference || 'first';
    
    // 🚫 TRIPLE CHECK: Validate each name before using
    if (firstName) {
      const validation = validatePersonalizationName(firstName);
      if (!validation.isClean) {
        console.error('🚨 CONTAMINATED firstName detected:', validation.contaminatedNames);
        debugNameResolution('CONTAMINATED_FIRST_NAME', cleanProfileData, null, firstName);
        return { displayName: 'there', isUsingProfileName: false };
      }
    }
    
    if (preferredName) {
      const validation = validatePersonalizationName(preferredName);
      if (!validation.isClean) {
        console.error('🚨 CONTAMINATED preferredName detected:', validation.contaminatedNames);
        debugNameResolution('CONTAMINATED_PREFERRED_NAME', cleanProfileData, null, preferredName);
        return { displayName: 'there', isUsingProfileName: false };
      }
    }
    
    console.log('🔒 STRICT NAME ENFORCEMENT - Profile Data Available:', {
      firstName,
      preferredName,
      preference,
      willUseFirstName: !!firstName
    });
    
    // Handle name usage preference - ONLY from profile
    if (preference === 'preferred' && preferredName) {
      console.log('🔒 STRICT NAME ENFORCEMENT: Using preferred name from profile:', preferredName);
      debugNameResolution('PREFERRED_NAME_SUCCESS', cleanProfileData, null, preferredName);
      return {
        displayName: preferredName,
        isUsingProfileName: true,
        usedPreferredName: true,
        nameUsageType: 'preferred'
      };
    } else if (preference === 'random' && firstName && preferredName) {
      // For random, alternate between first and preferred
      const usePreferred = Math.random() < 0.5;
      const selectedName = usePreferred ? preferredName : firstName;
      console.log('🔒 STRICT NAME ENFORCEMENT: Using random selection from profile:', selectedName);
      debugNameResolution('RANDOM_NAME_SUCCESS', cleanProfileData, null, selectedName);
      return {
        displayName: selectedName,
        isUsingProfileName: true,
        usedPreferredName: usePreferred,
        nameUsageType: 'random'
      };
    } else if (firstName) {
      console.log('🔒 STRICT NAME ENFORCEMENT: Using first name from profile:', firstName);
      debugNameResolution('FIRST_NAME_SUCCESS', cleanProfileData, null, firstName);
      return {
        displayName: firstName,
        isUsingProfileName: true,
        usedPreferredName: false,
        nameUsageType: 'first'
      };
    }
    
    console.log('⚠️ PROFILE DATA EXISTS BUT NO USABLE NAME FOUND!');
  } else {
    console.log('⚠️ NO PROFILE DATA PROVIDED - FALLING BACK TO METADATA');
  }

  // Fallback to user metadata ONLY if no profile data exists
  const metaFirstName = user.user_metadata?.first_name?.trim();
  const metaFullName = user.user_metadata?.full_name?.trim();
  
  if (metaFirstName) {
    console.log('🔒 STRICT NAME ENFORCEMENT: Using metadata first name:', metaFirstName);
    return {
      displayName: metaFirstName,
      isUsingProfileName: false
    };
  }
  
  if (metaFullName) {
    const firstName = metaFullName.split(' ')[0];
    console.log('🔒 STRICT NAME ENFORCEMENT: Using metadata full name first part:', firstName);
    return {
      displayName: firstName,
      isUsingProfileName: false
    };
  }

  // Generic fallback - NO NAME EXTRACTION FROM BIOGRAPHY EVER
  console.log('🔒 STRICT NAME ENFORCEMENT: Using generic fallback');
  debugNameResolution('GENERIC_FALLBACK', cleanProfileData, null, 'there');
  
  const result = {
    displayName: 'there',
    isUsingProfileName: false
  };
  
  // 🚫 FINAL CONTAMINATION CHECK
  if (result.displayName === 'Vinh') {
    console.error('🚨 CRITICAL ERROR: "Vinh" detected in final result!');
    result.displayName = 'there';
  }
  
  return result;
};

/**
 * Creates a personalized greeting for meditation sessions
 */
export const createPersonalizedGreeting = (
  nameResult: NamePersonalizationResult
): string => {
  if (nameResult.isUsingProfileName) {
    return `Welcome, ${nameResult.displayName}`;
  }
  
  // Generic greeting for non-authenticated users without names
  if (nameResult.displayName === 'there') {
    return 'Welcome';
  }
  
  return `Welcome, ${nameResult.displayName}`;
};